import authReducer from "./users/store"
import invitationsReducer from "./invitations/store"
import organisationsReducer from "./organisations/store"
import workspacesReducer from "./workspaces/store"
import customersReducer from "./customers/store"
import suppliersReducer from "./suppliers/store"
import membershipsReducer from "./memberships/store"
import permissionsReducer from "./permissions/store"
import contextOptionsReducer from "./context-options/store"
import rolesReducer from "./roles/store"
import hourReportTimerReducer from "./hour-reports/store"

import { createSelectorHook, useDispatch } from "react-redux"
import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { axiosInstance } from "./services/api-service"
import { Action, ThunkAction } from "@reduxjs/toolkit"
import { ResetAuthState } from "./users/store/actions"
import { api } from "./api"
import {
  hourReportTimerMiddleware,
  loadHourReportTimer,
  registerCrossTabSync,
} from "./hour-reports/hour-report-timer-service"

export const rootReducer = combineReducers({
  auth: authReducer,
  customers: customersReducer,
  invitations: invitationsReducer,
  memberships: membershipsReducer,
  orgs: organisationsReducer,
  suppliers: suppliersReducer,
  workspaces: workspacesReducer,
  permissions: permissionsReducer,
  contextOptions: contextOptionsReducer,
  roles: rolesReducer,
  hourReportTimer: hourReportTimerReducer,
  [api.reducerPath]: api.reducer,
})

const store = configureStore({
  reducer: rootReducer,
  middleware: function (getDefaultMiddleware) {
    const defaultMiddleware = getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    })
    return defaultMiddleware.prepend(api.middleware).prepend(hourReportTimerMiddleware)
  },
  preloadedState: {
    hourReportTimer: loadHourReportTimer() ?? undefined,
  },
})

store.subscribe(() => {
  const { accessToken } = store.getState().auth
  if (accessToken) {
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`
  }
})

store.subscribe(() => {
  const { authError } = store.getState().auth
  if (authError) store.dispatch(ResetAuthState())
})

registerCrossTabSync(store.dispatch)

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector = createSelectorHook<RootState>()

export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof store.dispatch
export type AppThunk = ThunkAction<void, RootState, null, Action<string>>
export type AppStore = typeof store

export default store
